import imsr from "@/imsr-api";
import store from "@/store/index.js";
import Content from "@/views/Content.vue";
import { Auth } from "aws-amplify";
import { trackRouter } from "vue-gtag-next";
import { createRouter, createWebHistory } from "vue-router";

const MAIN_TITLE = "PESCLE";
const routes = [
  {
    path: "/auth/signin",
    name: "authSignIn",
    meta: {
      title: "SignIn",
      requiredAuth: false,
    },
    component: () => import("@/views/Auth/AuthSignIn.vue"),
  },
  {
    path: "/auth/signup",
    name: "authSignUp",
    meta: {
      title: "SignUp",
      requiredAuth: false,
    },
    component: () => import("@/views/Auth/AuthSignUp.vue"),
  },
  {
    path: "/auth/confirm",
    name: "authConfirm",
    meta: {
      title: "SignUp",
      requiredAuth: false,
    },
    component: () => import("@/views/Auth/AuthConfirm.vue"),
  },
  {
    path: "/auth/forgot",
    name: "authForgotPassword",
    meta: {
      title: "SignIn",
      requiredAuth: false,
    },
    component: () => import("@/views/Auth/AuthForgotPassword.vue"),
  },
  {
    path: "/auth/forgot/confirm",
    name: "authForgotConfirm",
    meta: {
      title: "SignIn",
      requiredAuth: false,
    },
    component: () => import("@/views/Auth/AuthForgotConfirm.vue"),
  },
  {
    path: "/",
    component: Content,
    meta: {
      requiredAuth: true,
    },
    children: [
      {
        path: "/projects/list",
        name: "projectsList",
        meta: {
          title: "Project",
          requiredAuth: true,
        },
        component: () => import("@/views/Projects/ProjectsList.vue"),
      },
      {
        path: "/devices/list",
        name: "devicesAllList",
        meta: {
          title: "Devices",
          requiredSpaceAdmin: true,
        },
        component: () => import("@/views/Devices/DevicesAllList.vue"),
      },
      {
        path: "/users/list",
        name: "usersList",
        meta: {
          title: "Users",
          requiredSpaceAdmin: true,
        },
        component: () => import("@/views/Users/UsersList.vue"),
      },
      {
        path: "/users/usersView",
        name: "usersView",
        meta: {
          title: "Users",
          requiredSpaceAdmin: true,
        },
        component: () => import("@/views/Users/UserView/UsersView.vue"),
      },
      {
        path: "/projects/create",
        name: "projectCreate",
        meta: {
          title: "Project",
          requiredSpaceAdmin: true,
        },
        component: () => import("@/views/Projects/ProjectsCreate.vue"),
      },
      {
        path: "/projects/delete/:projectId",
        name: "projectDelete",
        meta: {
          title: "Project",
          requiredSpaceAdmin: true,
        },
        component: () => import("@/views/Projects/ProjectsDelete.vue"),
      },
      {
        path: "/:projectId/dashboard",
        name: "dashboard",
        meta: {
          title: "Dashboard",
          requiredAuth: true,
          requiredProject: true,
        },
        component: () => import("@/views/Dashboard/Dashboard.vue"),
      },
      {
        path: "/:projectId/events",
        name: "eventsList",
        meta: {
          title: "Events",
          requiredAuth: true,
          requiredProject: true,
        },
        component: () => import("@/views/Events/EventsList.vue"),
      },
      {
        path: "/:projectId/data",
        name: "dataList",
        meta: {
          title: "Data",
          requiredAuth: true,
          requiredProject: true,
        },
        component: () => import("@/views/Data/DataList.vue"),
      },
      {
        path: "/:projectId/download",
        name: "csvDownload",
        meta: {
          title: "Download",
          requiredAuth: true,
          requiredProject: true,
        },
        component: () => import("@/views/Download/CsvDownload.vue"),
      },
      {
        path: "/:projectId/data/:deviceId/:date?",
        name: "dataView",
        meta: {
          title: "Data",
          requiredAuth: true,
          requiredProject: true,
        },
        component: () => import("@/views/Data/DataView.vue"),
      },
      {
        path: "/:projectId/view",
        name: "projectsView",
        meta: {
          title: "Project",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Projects/ProjectsView.vue"),
      },
      {
        path: "/:projectId/devices",
        name: "devicesList",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesList.vue"),
      },
      {
        path: "/:projectId/devices/:deviceId",
        name: "devicesView",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesView/DevicesView.vue"),
      },
      {
        path: "/:projectId/devices/create",
        name: "devicesCreate",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesCreate.vue"),
      },
      {
        path: "/:projectId/devices/edit/:deviceId",
        name: "devicesEdit",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesEdit.vue"),
      },
      {
        path: "/:projectId/device/image-upload/:deviceId",
        name: "deviceImageUpload",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesImageUpload.vue"),
      },
      {
        path: "/:projectId/device/survey/:deviceId",
        name: "deviceSurvey",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesSurvey.vue"),
      },
      {
        path: "/:projectId/device/undetectedArea/:deviceId",
        name: "UndetectedArea",
        meta: {
          title: "Device",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/UndetectedArea.vue"),
      },
      {
        path: "/:projectId/devices/support",
        name: "devicesSupport",
        meta: {
          title: "Devices",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/Devices/DevicesSupport.vue"),
      },
      {
        path: "/:projectId/devices/faq",
        name: "faq",
        meta: {
          title: "FAQ",
          requiredAuth: true,
          requiredProjectAdmin: true,
          requiredProject: true,
        },
        component: () => import("@/views/FAQ/FAQ.vue"),
      },
      {
        path: "/profile/view",
        name: "profileView",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/ProfileView.vue"),
      },
      {
        path: "/profile/view/helpMfa",
        name: "helpMfaja",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/HelpMfaSettingJa.vue"),
      },
      {
        path: "/profile/view/helpMfa-en",
        name: "helpMfaen",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/HelpMfaSettingEn.vue"),
      },
      {
        path: "/profile/edit",
        name: "profileEdit",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/ProfileEdit.vue"),
      },
      {
        path: "/profile/startMfa",
        name: "profileStartMfa",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/ProfileStartMfa.vue"),
      },
      {
        path: "/profile/getToken",
        name: "profileGetToken",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/ProfileGetToken.vue"),
      },
      {
        path: "/profile/stopMfa",
        name: "profileStopMfa",
        meta: {
          title: "Profile",
          requiredAuth: true,
        },
        component: () => import("@/views/Profile/ProfileStopMfa.vue"),
      },
      {
        path: "/:projectId/:deviceId/movie/:eventDateTime",
        name: "movieView",
        meta: {
          title: "Movie",
          requiredAuth: true,
        },
        component: () => import("@/views/Movie/MovieView.vue"),
      },
      {
        path: "/:projectId/report/daily/:eventDate?/:deviceId?",
        name: "reportDialy",
        meta: {
          title: "Report",
          requiredAuth: true,
        },
        component: () => import("@/views/Reports/ReportsDaily.vue"),
      },
      {
        path: "/:projectId/report/weekly/:eventDate?/:deviceId?",
        name: "reportWeekly",
        meta: {
          title: "Report",
          requiredAuth: true,
        },
        component: () => import("@/views/Reports/ReportsWeekly.vue"),
      },
      {
        path: "/:projectId/report/monthly/:deviceId?",
        name: "reportMonthly",
        meta: {
          title: "Report",
          requiredAuth: true,
        },
        component: () => import("@/views/Reports/ReportsMonthly.vue"),
      },
    ],
  },
  {
    path: "/error",
    name: "InternalServerError",
    meta: { title: "Internal Server Error", notFound: true },
    component: () => import("@/views/Common/500.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    meta: { title: "Not Found", notFound: true },
    component: () => import("@/views/Common/404.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

trackRouter(router);

router.afterEach((to) => {
  let title = to.meta.title || "";
  document.title = MAIN_TITLE + " " + title;
});

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiredAuth);
  if (requiresAuth) {
    try {
      await Auth.currentAuthenticatedUser();
      if (
        to.fullPath == "/" &&
        store.getters.tenantId != null &&
        store.getters.projectId != null
      ) {
        return {
          name: "dashboard",
          params: { projectId: store.getters.projectId },
        };
      } else if (to.fullPath == "/") {
        return {
          name: "authSignIn",
        };
      }
    } catch (error) {
      return {
        name: "authSignIn",
        query: { redirect: to.fullPath },
      };
    }
  }

  const requiredProject = to.matched.some(
    (record) => record.meta.requiredProject
  );
  if (requiredProject) {
    try {
      const res = await imsr.imsrGetApi("/projects", {
        tenantId: store.getters.tenantId,
        projectId: to.params.projectId,
      });
      if (!res.projectId) {
        await store.commit("setProjectName", null);
        router.push({ name: "projectsList" });
      }
    } catch (error) {
      await store.commit("setProjectId", null);
      await store.commit("setProjectName", null);
      await store.commit("setProjectRole", null);
      return {
        name: "projectsList",
      };
    }
  }

  const requiredProjectAdmin = to.matched.some(
    (record) => record.meta.requiredProjectAdmin
  );

  if (requiredProjectAdmin) {
    try {
      const check = await imsr.getUserProject({
        tenantId: store.getters.tenantId,
        projectId: to.params.projectId,
      });
      if (!check) {
        router.push({ name: "NotFound" });
      }
    } catch (error) {
      return {
        name: "authSignIn",
        query: { redirect: to.fullPath },
      };
    }
  }

  const requiredSpaceAdmin = to.matched.some(
    (record) => record.meta.requiredSpaceAdmin
  );
  if (requiredSpaceAdmin) {
    try {
      const check = await imsr.getUser({
        tenantId: store.getters.tenantId,
      });
      if (!check) {
        router.push({ name: "NotFound" });
      }
    } catch (error) {
      return {
        name: "authSignIn",
        query: { redirect: to.fullPath },
      };
    }
  }
});

export default router;
